import { SubmitHandler, useForm } from 'react-hook-form';

import { joiResolver } from '@hookform/resolvers/joi';
import { useElements, useStripe } from '@stripe/react-stripe-js';

import { countriesByCode } from '../../../../components/FormControls/CountryPicker/countries';
import {
  confirmInstructorPaymentStatus,
  fetchInstructorPurchaseIntent,
} from '../../../../db/courses/actions';
import i18n, { i18nNS } from '../../../../i18n';
import { formatCurrency } from '../../../../utils/currency';
import { useRequestDispatch } from '../../../../utils/request-actions';
import { useCreateCourseContext } from '../../../Context';
import { CreateCourseStep } from '../../../types';
import { FormValues } from './types';
import { validationSchema } from './validators';

export interface UsePaymentDetailsContent {
  currency: 'usd' | 'inr';
  cost: number;
}

const usePaymentDetailsContent = ({ currency, cost }: UsePaymentDetailsContent) => {
  const requestDispatch = useRequestDispatch();

  const stripe = useStripe();
  const stripeElements = useElements();

  const {
    activeStep,
    closeDrawer,
    moveToPreviousStep: _moveToPreviousStep,
    moveToNextStep,
    isCreatingProCourse,
    setIsCreatingProCourse,
    numCourses,
    numStudents,
    setPaymentErrorMessage,
    setPaymentMethodSlotId,
  } = useCreateCourseContext();

  const isVisible = activeStep === CreateCourseStep.PAYMENT_DETAILS;
  const moveToPreviousStep = () => _moveToPreviousStep();

  const formMethods = useForm<FormValues>({
    resolver: joiResolver(validationSchema),
    defaultValues: {
      name: '',
      address: '',
      city: '',
      country: countriesByCode['US'],
      state: '',
      zip: '',
      tnc: false,
    },
    mode: 'all',
  });

  const { formState } = formMethods;
  const { errors } = formState;
  const tncError = errors['tnc']?.message;

  const makePayment: SubmitHandler<FormValues> = async (data) => {
    if (!stripe || !stripeElements) {
      throw new Error(i18n.t('card_is_required', { ns: i18nNS.COMMON }));
    }

    const defaultErrorMessage = i18n.t('something_went_wrong_please_try_again_later', { ns: i18nNS.COMMON });

    try {
      // Trigger form validation
      const { error: submitError } = await stripeElements.submit();
      if (submitError) {
        setPaymentErrorMessage(submitError.message ?? defaultErrorMessage);
        return;
      }

      const { name, address, city, country, state, zip } = data;

      const numOfCourses = parseInt(numCourses, 10);

      const [lowerLimit] = numStudents.split('-');
      const numOfstudents = parseInt(lowerLimit, 10);

      const { purchaseId, clientKey } = await requestDispatch(fetchInstructorPurchaseIntent, {
        purchaseType: 'instructorSlots',
        slots: {
          type: isCreatingProCourse ? 'pro' : 'basic',
          numCourses: numOfCourses,
          numStudents: numOfstudents,
        },
        customer: {
          name: name,
          address: {
            line1: address,
            city: city,
            country: country.code,
            state: state,
            zip: zip,
          },
        },
      });

      const result = await stripe.confirmPayment({
        elements: stripeElements,
        clientSecret: clientKey,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              address: { country: country.code, postal_code: zip, state, city, line1: address, line2: '' },
            },
          },
        },
        redirect: 'if_required',
      });

      const isFailed = Boolean(result.error);

      const statusResponse = await requestDispatch(confirmInstructorPaymentStatus, {
        purchaseId,
        status: isFailed ? 'failed' : 'success',
        errorMessage: result.error?.message,
      });

      if (isFailed) {
        setPaymentErrorMessage(result.error?.message || defaultErrorMessage);

        moveToNextStep({ skip: 1 });
      } else {
        const { courseType, slotId } = statusResponse;
        setIsCreatingProCourse(courseType === 'pro');
        setPaymentMethodSlotId(slotId);
        moveToNextStep();
      }
    } catch (error) {
      console.log(error);
      setPaymentErrorMessage(defaultErrorMessage);
      moveToNextStep({ skip: 1 });
    }
  };

  return {
    formMethods,
    closeDrawer,
    isVisible,
    moveToPreviousStep,
    formattedCost: cost > 0 ? formatCurrency(cost, currency) : '-',
    makePayment,
    tncError,
  };
};
export default usePaymentDetailsContent;
